import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from 'redux/store';
import axios from '../../utils/axios';
import { translator } from 'translation/useTranslator';
import {
  ExternalAuthentication,
  ExternalAuthenticationState
} from '../../@types/externalAuthentication';
import { rejectWithError } from '../dispatchError';

const initialState: ExternalAuthenticationState = {
  externalAuthenticationMethods: [],
  isLoading: {}
};
const slice = createSlice({
  name: 'externalAuthenticationMethods',
  initialState,
  reducers: {
    startLoading(state, action) {
      state.isLoading[action.payload] = true;
    },
    endLoading(state, action) {
      state.isLoading[action.payload] = false;
    },
    setExternalAuthenticationMethods(state, action) {
      state.externalAuthenticationMethods = action.payload;
    }
  }
});

export default slice.reducer;

export function getExternalAuthenticationMethods() {
  dispatch(slice.actions.startLoading('getExternalAuthenticationMethods'));
  return async () => {
    try {
      const response = await axios.get('/external-authentication-methods');
      dispatch(slice.actions.setExternalAuthenticationMethods(response.data));
      dispatch(slice.actions.endLoading('getExternalAuthenticationMethods'));
      return await Promise.resolve({
        defaultSuccessMessage:
          translator?.externalAuthenticationMethodsFetched() ??
          `External authentication methods fetched`
      });
    } catch (error: any) {
      dispatch(slice.actions.endLoading('getExternalAuthenticationMethods'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotFetchExternalAuthenticationMethods() ??
          `Could not fetch external authentication methods`
      });
    }
  };
}

type SaveExternalAuthenticationProps = {
  externalAuthentication: ExternalAuthentication;
};
export function saveExternalAuthentication({
  externalAuthentication
}: SaveExternalAuthenticationProps) {
  dispatch(slice.actions.startLoading('saveExternalAuthentication'));
  return async () => {
    try {
      if (externalAuthentication.id) {
        await axios.put(`/external-authentication-methods`, { ...externalAuthentication });
      } else {
        await axios.post(`/external-authentication-methods`, { ...externalAuthentication });
      }

      dispatch(getExternalAuthenticationMethods());
      dispatch(slice.actions.endLoading('saveExternalAuthentication'));
      return await Promise.resolve({
        defaultSuccessMessage:
          translator?.externalAuthenticationMethodSaved() ?? `External authentication method saved`
      });
    } catch (error: any) {
      dispatch(slice.actions.endLoading('saveExternalAuthentication'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotSaveExternalAuthenticationMethod() ??
          `Could not save external authentication method`
      });
    }
  };
}

export function deleteExternalAuthentication(externalAuthenticationId: string) {
  dispatch(slice.actions.startLoading('deleteExternalAuthentication'));
  return async () => {
    try {
      await axios.delete(`/external-authentication-methods/${externalAuthenticationId}`);
      dispatch(getExternalAuthenticationMethods());

      dispatch(slice.actions.endLoading('deleteExternalAuthentication'));
      return await Promise.resolve({
        defaultSuccessMessage:
          translator?.externalAuthenticationMethodDeleted() ??
          `External authentication method deleted`
      });
    } catch (error: any) {
      dispatch(slice.actions.endLoading('deleteExternalAuthentication'));
      return await rejectWithError({
        error: error,
        defaultErrorMessage:
          translator?.couldNotDeleteExternalAuthenticationMethod() ??
          `Could not delete external authentication method`
      });
    }
  };
}
